/**
 * Categories Service
 * Handles all category-related API calls
 */

import { apiService, ApiResponse } from './api.service';

export interface Category {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  isActive: boolean;
  sortOrder: number;
  menuTypeId: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    products: number;
  };
}

export interface CreateCategoryDto {
  name: string;
  description?: string;
  menuTypeId?: string;
  image?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateCategoryDto {
  name?: string;
  description?: string;
  menuTypeId?: string;
  image?: string;
  isActive?: boolean;
  sortOrder?: number;
}

class CategoriesService {
  /**
   * Create a new category
   */
  async addCategory(data: CreateCategoryDto): Promise<ApiResponse<Category>> {
    try {
      return await apiService.post<Category>('/categories', data);
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  }

  /**
   * Get all categories
   */
  async getCategories(params?: {
    page?: number;
    limit?: number;
    search?: string;
    menuTypeId?: string;
  }): Promise<ApiResponse<{ data: Category[]; meta: any }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.menuTypeId) queryParams.append('menuTypeId', params.menuTypeId);

      const url = `/categories?${queryParams.toString()}`;
      return await apiService.get<{ data: Category[]; meta: any }>(url);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  }

  /**
   * Get category by ID
   */
  async getCategory(id: string): Promise<ApiResponse<Category>> {
    try {
      return await apiService.get<Category>(`/categories/${id}`);
    } catch (error) {
      console.error('Failed to fetch category:', error);
      throw error;
    }
  }

  /**
   * Update category
   */
  async updateCategory(id: string, data: UpdateCategoryDto): Promise<ApiResponse<Category>> {
    try {
      return await apiService.patch<Category>(`/categories/${id}`, data);
    } catch (error) {
      console.error('Failed to update category:', error);
      throw error;
    }
  }

  /**
   * Delete category
   */
  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/categories/${id}`);
    } catch (error) {
      console.error('Failed to delete category:', error);
      throw error;
    }
  }

  /**
   * Toggle category active status
   */
  async toggleActive(id: string): Promise<ApiResponse<Category>> {
    try {
      return await apiService.patch<Category>(`/categories/${id}/toggle-active`, {});
    } catch (error) {
      console.error('Failed to toggle category status:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const categoriesService = new CategoriesService();
